/**
 * Every order of the platform as one Excel sheet, downloaded as
 * `abcdior_DDMMYYYY_HHmmss.xlsx`.
 */
import type { Cell, Row } from 'write-excel-file/browser';
import type { Platform, Role } from '@/shared/constants';
import type { StoreId } from '@/shared/config/app-config';
import { queryOrdersReport, type Order } from './api';
import { REPORT_COLUMNS, reportFileName, reportRow } from './logic';

/** One report value as a sheet cell: empty when missing, text otherwise. */
export const toCell = (value: unknown): Cell => {
  if (value == null || value === '') return null;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  )
    return value;
  return String(value);
};

export const reportSheet = (
  customizations: readonly Order[],
  role: Role,
): Row[] => {
  const seen = new Set<unknown>();
  const rows = customizations
    .filter((cus) => {
      if (seen.has(cus.id)) return false;
      seen.add(cus.id);
      return true;
    })
    .map((cus) => reportRow(cus, role));
  return [
    [...REPORT_COLUMNS],
    ...rows.map((row) => REPORT_COLUMNS.map((column) => toCell(row[column]))),
  ];
};

export const exportOrders = async (
  storeIds: readonly StoreId[],
  platform: Platform,
  role: Role,
): Promise<void> => {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const customizations = await queryOrdersReport({ storeIds, platform });
  await writeXlsxFile(reportSheet(customizations, role), {
    sheet: 'Sheet',
  }).toFile(reportFileName(new Date()));
};
